import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import PlantCard from '../components/PlantCard';
import { Plant } from '../types/plants';
import { API_BASE_URL } from '../config';
import { debounce } from 'lodash';

interface PaginationData {
  total: number;
  page: number;
  totalPages: number;
  limit: number;
}

const Botanicals = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [plants, setPlants] = useState<Plant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLetter, setSelectedLetter] = useState('All');
  const [medicinalFilter, setMedicinalFilter] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationData>({
    total: 0,
    page: 1,
    totalPages: 1,
    limit: 20
  });
  
  // Generate alphabet for filtering
  const alphabet = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));
  alphabet.unshift('All');

  // Debounced search function
  const debouncedFetchPlants = useCallback(
    debounce(async (params: URLSearchParams) => {
      try {
        setLoading(true);
        // Create a new URLSearchParams to avoid mutating the original
        const fetchParams = new URLSearchParams(params);
        // Add fields we want to include in the response
        fetchParams.append('fields', 'imageUrl');
        const response = await axios.get(`${API_BASE_URL}/api/plants`, { params: fetchParams });
        setPlants(response.data.data || []);
        setPagination({
          total: response.data.pagination.total,
          page: response.data.pagination.page,
          totalPages: response.data.pagination.totalPages,
          limit: response.data.pagination.limit
        });
      } catch (error) {
        console.error('Error fetching plants:', error);
        setPlants([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  // Update URL when letter filter changes
  const handleLetterClick = (letter: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    
    // Reset to first page when changing filters
    newSearchParams.set('page', '1');
    
    if (letter === 'All') {
      newSearchParams.delete('letter');
    } else {
      newSearchParams.set('letter', letter);
    }
    
    setSearchParams(newSearchParams);
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', newPage.toString());
    setSearchParams(newSearchParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Update selected letter when URL changes
  useEffect(() => {
    const letterParam = searchParams.get('letter') || 'All';
    setSelectedLetter(letterParam);
  }, [searchParams]);

  // Fetch plants when search params change
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    
    // Add search term to params if it exists
    if (searchTerm) {
      params.set('search', searchTerm);
    } else {
      params.delete('search');
    }
    
    // Update medicinal filter from URL
    const medicinalParam = searchParams.get('medicinal');
    if (medicinalParam) {
      setMedicinalFilter(medicinalParam);
    } else {
      setMedicinalFilter(null);
    }

    // Fetch plants with debouncing for search
    debouncedFetchPlants(params);
    
    // Cleanup debounce on unmount
    return () => {
      debouncedFetchPlants.cancel();
    };
  }, [searchParams, searchTerm, debouncedFetchPlants]);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Botanical Directory</h1>
      
      {/* Search and Filter */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <input
            type="text"
            placeholder="Search plants..."
            className="flex-1 p-2 border rounded-lg"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          
          {medicinalFilter && (
            <div className="flex items-center bg-blue-50 px-4 py-2 rounded-lg">
              <span className="text-sm text-blue-700">
                Showing plants with property: <span className="font-medium">{medicinalFilter}</span>
              </span>
              <button 
                onClick={() => {
                  const newSearchParams = new URLSearchParams(searchParams);
                  newSearchParams.delete('medicinal');
                  setSearchParams(newSearchParams);
                  setMedicinalFilter(null);
                }}
                className="ml-2 text-blue-500 hover:text-blue-700"
              >
                ✕
              </button>
            </div>
          )}
        </div>

        {/* Alphabet Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {alphabet.map(letter => {
            const newSearchParams = new URLSearchParams(searchParams);
            if (letter === 'All') {
              newSearchParams.delete('letter');
            } else {
              newSearchParams.set('letter', letter);
            }
            newSearchParams.delete('page'); // Reset to first page
            
            return (
              <Link
                key={letter}
                to={`/botanicals?${newSearchParams.toString()}`}
                className={`w-10 h-10 flex items-center justify-center rounded-full ${
                  selectedLetter === letter
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                } font-medium`}
                onClick={(e) => {
                  e.preventDefault();
                  handleLetterClick(letter);
                }}
              >
                {letter}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading plants...</p>
        </div>
      ) : plants.length > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {plants.map((plant) => (
              <PlantCard 
                key={plant._id} 
                plant={{
                  ...plant,
                  displayName: plant.latin_name 
                    ? `${plant.latin_name}${plant.common_names ? ` (${plant.common_names})` : ''}`
                    : plant.common_names || 'Unnamed Plant'
                }} 
                currentLetter={selectedLetter} 
              />
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="mt-8 flex justify-center">
              <nav className="flex items-center space-x-1">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  «
                </button>
                <button
                  onClick={() => handlePageChange(Math.max(1, pagination.page - 1))}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ‹
                </button>
                
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  // Show page numbers around current page
                  let pageNum;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-10 h-10 rounded-md ${
                        pagination.page === pageNum
                          ? 'bg-green-600 text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handlePageChange(Math.min(pagination.totalPages, pagination.page + 1))}
                  disabled={pagination.page === pagination.totalPages}
                  className="px-3 py-1 rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ›
                </button>
                <button
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={pagination.page === pagination.totalPages}
                  className="px-3 py-1 rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  »
                </button>
              </nav>
              
              <div className="ml-4 flex items-center text-sm text-gray-600">
                Page {pagination.page} of {pagination.totalPages}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">No plants found. Try a different search term or filter.</p>
        </div>
      )}
    </div>
  );
};

export default Botanicals;